import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { signInWithEmailAndPassword, MultiFactorResolver } from 'firebase/auth';
import { auth, getMultiFactorResolver, TotpMultiFactorGenerator } from '../../config/firebase';
import { useAuth, setSuppressAuthState } from '../../components/auth/AuthContext';
import { apiClient } from '../../services/api/client';
import './Login.css';

type BlockedReason = 'cloud_armor' | 'backend_down' | null;
type TempAccessStage = 'blocked' | 'auth' | 'requesting' | 'result';

const DURATION_OPTIONS = [1, 2, 4, 6, 8];

const Login: React.FC = () => {
  const { user, login, completeMfaLogin, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // IP blocking state
  const [blockedReason, setBlockedReason] = useState<BlockedReason>(null);

  // Temp access flow state
  const [tempAccessStage, setTempAccessStage] = useState<TempAccessStage>('blocked');
  const [tempDuration, setTempDuration] = useState(8);
  const [tempAccessResult, setTempAccessResult] = useState<any>(null);
  const [tempAccessError, setTempAccessError] = useState('');
  const [tempMfaResolver, setTempMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [tempOtpCode, setTempOtpCode] = useState('');

  // Propagation countdown (5 min = 300s, starts when temp access is granted)
  const [propagationCountdown, setPropagationCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (propagationCountdown > 0) {
      countdownRef.current = setInterval(() => {
        setPropagationCountdown((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [propagationCountdown > 0]); // only re-run when transitioning to/from 0

  // Two-step IP access check
  useEffect(() => {
    const checkIPAccess = async () => {
      try {
        await apiClient.whitelistCheck();
        // IP is whitelisted — no block
        setBlockedReason(null);
      } catch {
        // whitelistCheck failed — determine if Cloud Armor or backend down
        try {
          await apiClient.accessCheck();
          // accessCheck succeeded → backend is up, Cloud Armor is blocking
          setBlockedReason('cloud_armor');
        } catch {
          // Both failed → backend is down
          setBlockedReason('backend_down');
        }
      } finally {
        setCheckingAccess(false);
      }
    };

    checkIPAccess();
  }, []);

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor ingrese email y contraseña');
      return;
    }

    setIsLoggingIn(true);
    setError('');

    try {
      await login(email, password);
    } catch (err: any) {
      if (err?.code === 'mfa-required' && err?.resolver) {
        setMfaResolver(err.resolver);
        setError('');
        setIsLoggingIn(false);
        return;
      }
      setError('Credenciales inválidas');
      setIsLoggingIn(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setError('Ingrese el código de 6 dígitos');
      return;
    }

    setIsLoggingIn(true);
    setError('');

    try {
      await completeMfaLogin(mfaResolver!, otpCode);
    } catch {
      setError('Código inválido. Intente nuevamente.');
      setIsLoggingIn(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaResolver(null);
    setOtpCode('');
    setError('');
  };

  // --- Temp access flow handlers ---

  const handleTempAccessAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setTempAccessError('Por favor ingrese email y contraseña');
      return;
    }

    setIsLoggingIn(true);
    setTempAccessError('');

    try {
      // Suppress AuthContext from processing onAuthStateChanged during temp access
      setSuppressAuthState(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      await requestTempAccessWithToken(idToken);
    } catch (err: any) {
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);
        setTempMfaResolver(resolver);
        setTempAccessError('');
        setIsLoggingIn(false);
        return;
      }
      setSuppressAuthState(false);
      setTempAccessError('Credenciales inválidas');
      setIsLoggingIn(false);
    }
  };

  const handleTempMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempOtpCode || tempOtpCode.length !== 6) {
      setTempAccessError('Ingrese el código de 6 dígitos');
      return;
    }

    setIsLoggingIn(true);
    setTempAccessError('');

    try {
      setSuppressAuthState(true);
      const hint = tempMfaResolver!.hints[0];
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, tempOtpCode);
      const userCredential = await tempMfaResolver!.resolveSignIn(assertion);
      const idToken = await userCredential.user.getIdToken();
      await requestTempAccessWithToken(idToken);
    } catch {
      setSuppressAuthState(false);
      setTempAccessError('Código inválido. Intente nuevamente.');
      setIsLoggingIn(false);
    }
  };

  const requestTempAccessWithToken = async (idToken: string) => {
    setTempAccessStage('requesting');
    try {
      const result = await apiClient.requestTempAccess(idToken, tempDuration);
      setTempAccessResult(result);
      setPropagationCountdown(300); // 5 minutes
      setTempAccessStage('result');
    } catch (err: any) {
      setTempAccessError(err.message || 'Error al solicitar acceso temporal');
      setTempAccessStage('result');
    } finally {
      // Sign out from Firebase — this was only for getting the token, not a full app login
      try { await auth.signOut(); } catch { /* ignore */ }
      setSuppressAuthState(false);
      setIsLoggingIn(false);
    }
  };

  const handleRetryWhitelistCheck = async () => {
    // Remember whether we already had a successful temp access grant
    const hadSuccessfulGrant = tempAccessResult && !tempAccessError;
    setCheckingAccess(true);
    setBlockedReason(null);
    try {
      await apiClient.whitelistCheck();
      // IP is now whitelisted — clear temp access state
      setBlockedReason(null);
      setTempAccessStage('blocked');
      setTempAccessResult(null);
      setTempAccessError('');
    } catch {
      try {
        await apiClient.accessCheck();
        setBlockedReason('cloud_armor');
        if (hadSuccessfulGrant) {
          // Rule hasn't propagated yet — stay on result page with countdown
          setTempAccessStage('result');
        } else {
          setTempAccessStage('blocked');
        }
      } catch {
        setBlockedReason('backend_down');
      }
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleBackToBlocked = () => {
    setTempAccessStage('blocked');
    setTempAccessError('');
    setTempMfaResolver(null);
    setTempOtpCode('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
  };

  // --- Loading state ---
  if (loading || checkingAccess) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-loading">
            <div className="login-loading-circle" />
            <p>Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Backend down ---
  if (blockedReason === 'backend_down') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
            <h2>Servicio No Disponible</h2>
          </div>
          <div className="error-message" style={{ marginTop: '20px', padding: '20px' }}>
            <p style={{ marginBottom: '10px', fontSize: '16px' }}>
              El servicio no está disponible en este momento.
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Por favor intente nuevamente en unos minutos.
            </p>
          </div>
          <button
            className="login-button"
            style={{ marginTop: '20px', width: '100%' }}
            onClick={handleRetryWhitelistCheck}
          >
            Reintentar
          </button>
          <div className="login-footer">
            <p>Contacto: soporte técnico</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Cloud Armor blocked ---
  if (blockedReason === 'cloud_armor') {

    // Stage: Requesting (spinner)
    if (tempAccessStage === 'requesting') {
      return (
        <div className="login-container">
          <div className="login-card">
            <div className="login-loading">
              <div className="login-loading-circle" />
              <p>Solicitando acceso temporal...</p>
            </div>
          </div>
        </div>
      );
    }

    // Stage: Result
    if (tempAccessStage === 'result') {
      const success = tempAccessResult && !tempAccessError;
      return (
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
              <h2>{success ? 'Acceso Concedido' : 'Error'}</h2>
            </div>
            {success ? (
              <>
                <div className="temp-access-success" style={{ marginTop: '20px', padding: '20px' }}>
                  <p style={{ marginBottom: '10px', fontSize: '16px' }}>
                    Acceso temporal concedido por {tempAccessResult.durationHours} horas.
                  </p>
                  <p style={{ fontSize: '14px', color: '#999', marginBottom: '10px' }}>
                    Su acceso expirará a las {new Date(tempAccessResult.expiresAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}.
                  </p>
                  <p style={{ fontSize: '13px', color: '#999' }}>
                    La regla puede tardar <strong style={{ color: '#ffaa00' }}>hasta 5 minutos</strong> en activarse.
                  </p>
                  {propagationCountdown > 0 && (
                    <p style={{ fontSize: '20px', fontWeight: 700, color: '#ffaa00', marginTop: '12px' }}>
                      {Math.floor(propagationCountdown / 60)}:{String(propagationCountdown % 60).padStart(2, '0')}
                    </p>
                  )}
                </div>
                <button
                  className="login-button"
                  style={{ marginTop: '20px', width: '100%' }}
                  onClick={handleRetryWhitelistCheck}
                >
                  {propagationCountdown > 0 ? 'Reintentar acceso' : 'Acceder'}
                </button>
              </>
            ) : (
              <>
                <div className="error-message" style={{ marginTop: '20px', padding: '20px' }}>
                  <p style={{ fontSize: '14px' }}>
                    {tempAccessError}
                  </p>
                </div>
                <button
                  className="login-button temp-access-secondary-button"
                  style={{ marginTop: '20px', width: '100%' }}
                  onClick={handleBackToBlocked}
                >
                  Volver
                </button>
              </>
            )}
            <div className="login-footer">
              <p>Contacto: soporte técnico</p>
            </div>
          </div>
        </div>
      );
    }

    // Stage: Auth (with MFA sub-stage)
    if (tempAccessStage === 'auth') {

      // MFA challenge for temp access
      if (tempMfaResolver) {
        return (
          <div className="login-container">
            <div className="login-card">
              <div className="login-header">
                <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
                <p>Verificación en dos pasos</p>
              </div>
              <form onSubmit={handleTempMfaSubmit} className="login-form">
                <p style={{ textAlign: 'center', marginBottom: '16px', color: '#555', fontSize: '14px' }}>
                  Ingrese el código de 6 dígitos de su aplicación autenticadora
                </p>
                <div className="form-group">
                  <label htmlFor="tempOtpCode">Código de verificación</label>
                  <input
                    type="text"
                    id="tempOtpCode"
                    value={tempOtpCode}
                    onChange={(e) => setTempOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    disabled={isLoggingIn}
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }}
                  />
                </div>
                {tempAccessError && <div className="error-message">{tempAccessError}</div>}
                <button
                  type="submit"
                  className="login-button"
                  disabled={isLoggingIn || tempOtpCode.length !== 6}
                >
                  {isLoggingIn ? 'Verificando...' : 'Verificar'}
                </button>
                <button
                  type="button"
                  className="login-link-button"
                  onClick={handleBackToBlocked}
                  disabled={isLoggingIn}
                  style={{
                    background: 'none', border: 'none', color: '#1976d2',
                    cursor: 'pointer', marginTop: '12px', width: '100%',
                    fontSize: '14px',
                  }}
                >
                  ← Volver
                </button>
              </form>
              <div className="login-footer">
                <p>Contacto: soporte técnico</p>
              </div>
            </div>
          </div>
        );
      }

      // Email + password + duration selector
      return (
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
              <h2>Solicitar Acceso Temporal</h2>
              <p>Autentíquese para solicitar acceso desde su IP actual</p>
            </div>
            <form onSubmit={handleTempAccessAuth} className="login-form">
              <div className="form-group">
                <label htmlFor="tempEmail">Email</label>
                <input
                  type="email"
                  id="tempEmail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="usuario@empresa.com"
                  disabled={isLoggingIn}
                />
              </div>
              <div className="form-group">
                <label htmlFor="tempPassword">Contraseña</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="tempPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={isLoggingIn}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={isLoggingIn}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="tempDuration">Duración del acceso</label>
                <select
                  id="tempDuration"
                  value={tempDuration}
                  onChange={(e) => setTempDuration(Number(e.target.value))}
                  disabled={isLoggingIn}
                  className="temp-access-select"
                >
                  {DURATION_OPTIONS.map((h) => (
                    <option key={h} value={h}>{h} {h === 1 ? 'hora' : 'horas'}</option>
                  ))}
                </select>
              </div>
              {tempAccessError && <div className="error-message">{tempAccessError}</div>}
              <button
                type="submit"
                className="login-button"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Autenticando...' : 'Solicitar Acceso'}
              </button>
              <button
                type="button"
                className="login-link-button"
                onClick={handleBackToBlocked}
                disabled={isLoggingIn}
                style={{
                  background: 'none', border: 'none', color: '#1976d2',
                  cursor: 'pointer', marginTop: '12px', width: '100%',
                  fontSize: '14px',
                }}
              >
                ← Volver
              </button>
            </form>
            <div className="login-footer">
              <p>Contacto: soporte técnico</p>
            </div>
          </div>
        </div>
      );
    }

    // Stage: Blocked (default — with temp access button)
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
            <h2>Acceso Denegado</h2>
          </div>
          <div className="error-message" style={{ marginTop: '20px', padding: '20px' }}>
            <p style={{ marginBottom: '10px', fontSize: '16px' }}>
              Su dirección IP no está autorizada para acceder a este sistema.
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Puede solicitar acceso temporal autenticándose con sus credenciales.
            </p>
          </div>
          <button
            className="login-button"
            style={{ marginTop: '20px', width: '100%' }}
            onClick={() => setTempAccessStage('auth')}
          >
            Solicitar Acceso Temporal
          </button>
          <div className="login-footer">
            <p>Contacto: soporte técnico</p>
          </div>
        </div>
      </div>
    );
  }

  // MFA challenge step (normal login)
  if (mfaResolver) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
            <img src="/fx-plus-logo.svg" alt="FX+" className="login-app-logo" />
            <p>Verificación en dos pasos</p>
          </div>

          <form onSubmit={handleMfaSubmit} className="login-form">
            <p style={{ textAlign: 'center', marginBottom: '16px', color: '#555', fontSize: '14px' }}>
              Ingrese el código de 6 dígitos de su aplicación autenticadora
            </p>
            <div className="form-group">
              <label htmlFor="otpCode">Código de verificación</label>
              <input
                type="text"
                id="otpCode"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                disabled={isLoggingIn}
                autoFocus
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="login-button"
              disabled={isLoggingIn || otpCode.length !== 6}
            >
              {isLoggingIn ? 'Verificando...' : 'Verificar'}
            </button>

            <button
              type="button"
              className="login-link-button"
              onClick={handleBackToLogin}
              disabled={isLoggingIn}
              style={{
                background: 'none', border: 'none', color: '#1976d2',
                cursor: 'pointer', marginTop: '12px', width: '100%',
                fontSize: '14px'
              }}
            >
              ← Volver al inicio de sesión
            </button>
          </form>

          <div className="login-footer">
            <p>¿Necesita ayuda? Contacte a soporte técnico</p>
          </div>
        </div>
      </div>
    );
  }

  // Normal login form
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/palace.jpg" alt="Palace Technology" className="login-logo" />
          <img src="/fx-plus-logo.svg" alt="FX+" className="login-app-logo" />
          <p>Ingrese sus credenciales para acceder</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com"
              disabled={isLoggingIn}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoggingIn}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                disabled={isLoggingIn}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="login-button"
            disabled={isLoggingIn}
          >
            {isLoggingIn ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        <div className="login-footer">
          <p>¿Necesita ayuda? Contacte a soporte técnico</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
